import axios from "axios";
import { urlFor } from "~/client";
import { createDiagnosticsQueue, type DiagnosticsContext } from "~/lib/diagnostics-queue";

/**
 * Envío por lotes de la telemetría del dashboard del agente (ciclo de vida del
 * SSE) al log del servidor (→ Loki, greppable como "[dashboard]").
 *
 * Por qué existe: el widget embebido ya enviaba la suya, pero el dashboard no,
 * así que producción era CIEGA al estado del realtime de los agentes. Los cuatro
 * modos de fallo conocidos del transporte (re-subscribe fallido que deja el
 * canal sordo, uid envenenado, ventanas sin backfill y EventSource zombi) se ven
 * todos EXACTAMENTE igual desde fuera: "no me llegan los mensajes hasta que
 * recargo". Sin estas líneas no hay forma de distinguirlos en prod.
 *
 * El envío es best-effort a propósito: un lote perdido se descarta en silencio y
 * nunca se reintenta — la telemetría jamás debe degradar la app que observa.
 */

const MAX_QUEUE = 200;
const MAX_BATCH = 25;
const FLUSH_INTERVAL_MS = 3_000;

const queue = createDiagnosticsQueue({ maxQueue: MAX_QUEUE, maxBatch: MAX_BATCH });

let flushTimer: number | null = null;
let flushing = false;
let pagehideBound = false;

function endpoint(): string | null {
  // El provider de Transmit monta en la raíz, así que también vive en pantallas
  // sin workspace (login, onboarding). La ruta lleva `:company_slug`, y ahí no
  // hay ninguno: comprobarlo explícitamente en vez de confiar en que `urlFor`
  // lance —no lo garantiza, y de hecho puede devolver una URL con el parámetro
  // sin sustituir— es lo que ya hacen `xsrf.ts` y el beacon de `conversation.tsx`.
  if (typeof window === "undefined") {
    return null;
  }
  if (!/^\/w\/[^/]+/.test(window.location.pathname)) {
    return null;
  }

  return urlFor("diagnostics.client-logs");
}

function scheduleFlush(): void {
  if (flushTimer !== null || typeof window === "undefined") {
    return;
  }

  flushTimer = window.setTimeout(() => {
    flushTimer = null;
    void flush();
  }, FLUSH_INTERVAL_MS);
}

async function flush(): Promise<void> {
  if (flushing) {
    return;
  }

  const url = endpoint();
  if (url === null) {
    return;
  }

  const entries = queue.takeBatch();
  if (entries.length === 0) {
    return;
  }

  flushing = true;
  try {
    await axios.post(url, { entries }, { headers: { "Content-Type": "application/json" } });
  } catch {
    // @expected-error-boundary: best-effort por diseño — ver cabecera.
  } finally {
    flushing = false;
  }

  scheduleFlush();
}

export function logDashboardDiagnostic(event: string, context?: DiagnosticsContext): void {
  queue.enqueue(event, context, new Date().toISOString());
  scheduleFlush();

  if (!pagehideBound && typeof window !== "undefined") {
    pagehideBound = true;
    // Último intento al cerrar/ocultar la pestaña: la cola de un SSE que acaba
    // de morir es justo la que más interesa y se perdería con el unload.
    window.addEventListener("pagehide", () => {
      void flush();
    });
  }
}
